import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { setupRecaptcha } from '../firebase';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import { motion, AnimatePresence } from 'motion/react';

export default function SignInModal({ 
  isOpen, 
  onClose,
  defaultTab = 'login'
}: { 
  isOpen: boolean; 
  onClose: () => void;
  defaultTab?: 'login' | 'signup';
}) {
  const { signInWithGoogle, signInWithFacebook, signInWithPhone, signUpWithEmail } = useAuth();

  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(defaultTab);

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);
  const [isPhoneFlow, setIsPhoneFlow] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneSent, setPhoneSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [isPhoneSending, setIsPhoneSending] = useState(false);
  const [isPhoneVerifying, setIsPhoneVerifying] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'buyer' | 'seller'>('buyer');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = () => {
    setActiveTab('login');
    setIsPhoneFlow(false);
    setPhoneNumber('');
    setPhoneCode('');
    setPhoneSent(false);
    setConfirmationResult(null);
    setPhoneError(null);
    setSignUpError(null);
    setFullName('');
    setEmail('');
    setPassword('');
    setRole('buyer');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      handleClose();
    } catch (error: any) {
      console.error('Google sign-in failed', error);
      setSignUpError('Google sign-in failed. Please try again.');
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      await signInWithFacebook();
      handleClose();
    } catch (error: any) {
      console.error('Facebook sign-in failed', error);
      setSignUpError('Facebook sign-in failed. Please try again.');
    }
  };

  const handlePhoneSend = async () => {
    setPhoneError(null);
    if (!phoneNumber) {
      setPhoneError('Enter your phone number first.');
      return;
    }

    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    if (!normalizedPhone) {
      setPhoneError('Please enter a valid phone number (e.g. 07XXXXXXXX or +254XXXXXXXXX).');
      return;
    }

    setIsPhoneSending(true);

    try {
      const appVerifier = await setupRecaptcha('recaptcha-container');
      const result = await signInWithPhone(normalizedPhone, appVerifier);
      setConfirmationResult(result);
      setPhoneSent(true);
    } catch (error: any) {
      console.error('Phone sign-in failed', error);
      setPhoneError(
        'Could not send verification code. Please check your phone number and try again.'
      );
    } finally {
      setIsPhoneSending(false);
    }
  };

  const handlePhoneVerify = async () => {
    if (!confirmationResult) {
      setPhoneError('Please request a code first.');
      return;
    }

    if (!phoneCode) {
      setPhoneError('Enter the SMS code you received.');
      return;
    }

    setIsPhoneVerifying(true);

    try {
      await confirmationResult.confirm(phoneCode);
      handleClose();
    } catch (error: any) {
      console.error('Phone verification failed', error);
      setPhoneError('The code is invalid or expired. Please try again.');
    } finally {
      setIsPhoneVerifying(false);
    }
  };

  const handleEmailSignUp = async () => {
    setSignUpError(null);
    setIsSubmitting(true);

    if (!fullName || !email || !password) {
      setSignUpError('Please fill in all required fields.');
      setIsSubmitting(false);
      return;
    }

    try {
      await signUpWithEmail(email, password, fullName, role);
      handleClose();
    } catch (error: any) {
      console.error('Email signup failed', error);
      setSignUpError('Unable to create account. Please check your details and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence onExitComplete={resetState}>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row z-[10000] max-h-[90vh] overflow-y-auto"
          >
            <div className="hidden md:flex relative w-full md:w-[45%] bg-[#FF5722] p-8 sm:p-10 text-white overflow-hidden flex-col justify-between">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4"></div>

              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-12">
                  <div className="bg-white p-2 rounded-xl">
                    <span className="text-[#FF5722] font-black text-xl leading-none">P</span>
                  </div>
                  <span className="font-black text-2xl tracking-tight">Pambo.biz</span>
                </div>

                <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-8">
                  Kenya's Largest
                  <br />
                  Trade Hub
                </h2>

                <div className="space-y-6">
                  {[
                    'Verified Suppliers & Buyers',
                    'Global & Local Trade Hubs',
                    'Safe local trade connections',
                    'Direct buyer & seller communication',
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="mt-1 bg-white/20 p-1 rounded-full">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="font-semibold text-white/90">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative z-10 mt-12 pt-8 border-t border-white/20">
                <p className="font-bold text-white/80">
                  Trusted by over 10,000+ businesses in Kenya.
                </p>
              </div>
            </div>

            <div className="w-full md:w-[55%] p-6 sm:p-12 relative bg-white flex flex-col justify-center">
              <button
                type="button"
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="max-w-sm mx-auto w-full">
                {/* Mobile Branding */}
                <div className="flex justify-center items-center gap-2 mb-6 md:hidden">
                  <div className="bg-[#FF5722] p-2 rounded-xl">
                    <span className="text-white font-black text-xl leading-none">P</span>
                  </div>
                  <span className="font-black text-2xl tracking-tight text-slate-800">
                    Pambo.biz
                  </span>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-800 mb-2">
                    Welcome to Pambo
                  </h3>
                  <p className="text-slate-500 font-medium text-sm sm:text-base">
                    Sign in or create an account to start trading.
                  </p>
                </div>

                <div className="mb-8 grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('login')}
                    className={`rounded-2xl py-3 font-semibold transition ${activeTab === 'login' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('signup')}
                    className={`rounded-2xl py-3 font-semibold transition ${activeTab === 'signup' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                  >
                    Sign Up
                  </button>
                </div>

                {activeTab === 'login' ? (
                  <div className="space-y-4">
                    {!isPhoneFlow ? (
                      <>
                        <button
                          type="button"
                          onClick={handleGoogleSignIn}
                          className="w-full flex items-center justify-center gap-3 px-6 py-4 border-2 border-gray-100 rounded-2xl hover:border-orange-500 hover:bg-orange-50 transition-all group font-bold text-slate-700"
                        >
                          <img
                            src="https://img.icons8.com/color/48/000000/google-logo.png"
                            alt="Google"
                            className="w-6 h-6 group-hover:scale-110 transition-transform"
                          />
                          <span>Continue with Google</span>
                        </button>

                        <button
                          type="button"
                          onClick={handleFacebookSignIn}
                          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1877F2] text-white rounded-2xl hover:bg-[#166FE5] transition-all font-bold"
                        >
                          <img
                            src="https://img.icons8.com/ios-filled/50/ffffff/facebook-new.png"
                            alt="Facebook"
                            className="w-6 h-6"
                          />
                          <span>Continue with Facebook</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setIsPhoneFlow(true)}
                          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 transition-all font-bold"
                        >
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          <span>Continue with Phone Number</span>
                        </button>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Phone number
                          </label>
                          <input
                            value={phoneNumber}
                            onChange={(event) => setPhoneNumber(event.target.value)}
                            placeholder="e.g. 07XXXXXXXX"
                            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-700 outline-none focus:border-orange-500"
                          />
                        </div>

                        {phoneSent ? (
                          <>
                            <div>
                              <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Verification code
                              </label>
                              <input
                                value={phoneCode}
                                onChange={(event) => setPhoneCode(event.target.value)}
                                placeholder="Enter SMS code"
                                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-700 outline-none focus:border-orange-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handlePhoneVerify}
                              disabled={isPhoneVerifying}
                              className="w-full px-6 py-4 rounded-2xl bg-orange-500 text-white font-bold hover:bg-orange-600 transition-all disabled:opacity-60"
                            >
                              {isPhoneVerifying ? 'Verifying code...' : 'Verify Code'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsPhoneFlow(false);
                                setPhoneSent(false);
                                setPhoneCode('');
                                setPhoneNumber('');
                              }}
                              className="w-full px-6 py-4 rounded-2xl border border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-all"
                            >
                              Back to sign-in options
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={handlePhoneSend}
                              disabled={isPhoneSending}
                              className="w-full px-6 py-4 rounded-2xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition-all disabled:opacity-60"
                            >
                              {isPhoneSending ? 'Sending code...' : 'Send verification code'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsPhoneFlow(false)}
                              className="w-full px-6 py-4 rounded-2xl border border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-all"
                            >
                              Back to sign-in options
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Full Name
                      </label>
                      <input
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        placeholder="Jane Doe"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-700 outline-none focus:border-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Email address
                      </label>
                      <input
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-700 outline-none focus:border-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Create a password"
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-700 outline-none focus:border-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Account type
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setRole('buyer')}
                          className={`rounded-2xl py-3 font-semibold transition ${role === 'buyer' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700'}`}
                        >
                          Buyer
                        </button>
                        <button
                          type="button"
                          onClick={() => setRole('seller')}
                          className={`rounded-2xl py-3 font-semibold transition ${role === 'seller' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700'}`}
                        >
                          Seller
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleEmailSignUp}
                      disabled={isSubmitting}
                      className="w-full px-6 py-4 rounded-2xl bg-orange-500 text-white font-bold hover:bg-orange-600 transition-all disabled:opacity-60"
                    >
                      {isSubmitting ? 'Creating account...' : 'Create account with email'}
                    </button>
                  </div>
                )}

                {(signUpError || phoneError) && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {signUpError || phoneError}
                  </div>
                )}

                <div className="mt-8 text-center text-xs font-medium text-slate-500">
                  By continuing, you agree to Pambo's{' '}
                  <a href="/terms" className="text-orange-500 hover:underline">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" className="text-orange-500 hover:underline">
                    Privacy Policy
                  </a>
                  .
                </div>
              </div>

              <div
                id="recaptcha-container"
                className="absolute inset-x-0 bottom-0 h-0 overflow-hidden"
              />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
